import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Moment from "./modules/moment.module";

import translations from "./i18n/translations.json";

const resources = Object.entries(translations).reduce((acc, [id, langs]) => {
  Object.entries(langs).forEach(([lang, message]) => {
    if (!acc[lang]) {
      acc[lang] = { translation: {} };
    }

    acc[lang].translation[id] = message;
  });

  return acc;
}, {});

i18n.use(initReactI18next).init({
  lng: "sv",
  fallbackLng: "en",
  debug: false,
  keySeparator: false,
  interpolation: {
    escapeValue: false, // not needed for react!!
  },
  resources,
  react: {
    transKeepBasicHtmlNodesFor: [
      "br",
      "strong",
      "i",
      "em",
      "u",
      "a",
      "li",
      "ul",
      "ol",
      "p",
      "s",
      "h1",
      "h2",
      "h3",
      "h4",
      "pre",
    ],
  },
});

export const formatNumber = (
  number,
  intlNumberFormatOpts = {
    style: "decimal",
    maximumFractionDigits: 2,
  },
) => {
  if (isNaN(number)) return "";

  return new Intl.NumberFormat(i18n.language, intlNumberFormatOpts).format(
    number,
  );
};

export const formatInteger = (value, options = {}) =>
  formatNumber(value, { ...options, maximumFractionDigits: 0 });

export const formatCurrency = (value, options = {}) =>
  formatNumber(value, {
    style: "currency",
    currency: "SEK",
    ...options,
  });

export const formatPercentage = (value, options = {}) =>
  formatNumber(value, {
    style: "percent",
    maximumFractionDigits: 2,
    ...options,
  });

export const formatDate = (
  date,
  intlDateFormatOpts = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  },
) => {
  if (!date) return "";

  const normalizedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(normalizedDate.getTime())) return "";

  return new Intl.DateTimeFormat(i18n.language, intlDateFormatOpts).format(
    normalizedDate,
  );
};

// maxRelativeAge expects { value: number, unit: string }, where unit is a
// Moment-compatible diff unit like "hours", "days", "weeks", or "months".
// When the date exceeds that age, format is passed to formatDate as the fallback.
export const formatDateRelative = (date, { maxRelativeAge, format } = {}) => {
  if (!date) return "";

  const momentDate = Moment(date);

  if (!momentDate.isValid()) return "";

  if (
    maxRelativeAge &&
    Math.abs(Moment().diff(momentDate, maxRelativeAge.unit, true)) >
      maxRelativeAge.value
  ) {
    return formatDate(date, format);
  }

  return momentDate.fromNow();
};

export const { t } = i18n;

export default i18n;
